/*
 * Copyright 2025 Intuition Robotics ltd
 */
import type {DB_Object} from "@intuitionrobotics/firebase";
import {GADQuestions} from "./anxiety.js";
import {PHQQuestions} from "./depression.js";
import type {
	CalendarEvent,
	ReminderStatus
} from "@app/ir-q-app-common/types/calendar_event";
import type {ObjectTS} from "@intuitionrobotics/ts-common";


export type BasePatientData = DB_Object & {
	unitId: string
	timestamp: number
	date: string
	label: string
	raw_value?: number
	raw_value_qual?: string
	key: TableKeys
	extraData?: any
}
export type DB_PatientData = BasePatientData & {
	value: number
}
export type DB_PatientDataForCSV = BasePatientData & {value: string}

export enum PainQual {
	NO_PAIN        = "noPain",
	MILD           = "mild",
	MODERATE       = "moderate",
	SEVERE         = "severe",
	WORST_POSSIBLE = "worstPossible",
}

export const DayFormat = "D/M/YYYY";
// export type DB_PatientData<k extends keyof extraDataMap> = DB_Object & {
// 	unitId: string
// 	timestamp: number
// 	label: string
// 	value: number
// 	key: k
// 	extraData?: extraDataMap[k]
// }
//
// //eventually this will replace enum
// export type extraDataMap = {
// 	bp_measure: undefined,
// 	stress_measure: undefined,
// 	sleep_pattern: { description: string },
// 	exercise_measure: undefined,
// 	medications: { timeOfDay: string },
// 	mood_pattern: { description: string }
// }

export enum TableKeys {
	BloodPressure           = "v_bp_measure",
	SelfReport              = "v_self_report",
	Stress                  = "v_stress_measure",
	Sleep                   = "v_sleep_pattern",
	Exercise                = "v_exercise_measure",
	Mood                    = "v_mood_pattern",
	LastUserAction          = "v_last_user_action",
	CheckIn                 = "v_check_in",
	PsychologicalAssessment = "v_psychological_assessment",
	DidCareCheckin          = 'v_did_care_checkin',
	Pain                    = "v_pain_assessment",
}

export enum PsychologicalMemoryNames {
	Anxiety    = "anxiety_assessment",
	Depression = "depression_assessment",
}

export const psychologicalTypeMap = (type: PsychologicalMemoryNames, patientName?: string, score?: number): {
	questions: PsychologicalQuestion[],
	subject: string,
	details: string
} | undefined => {
	switch (type) {
		case PsychologicalMemoryNames.Anxiety:
			return {
				questions: GADQuestions,
				subject: "New GAD-2 score collected",
				details: `${patientName} scored ${score} on the GAD-2 questionnaire`
			};
		case PsychologicalMemoryNames.Depression:
			return {
				questions: PHQQuestions,
				subject: "New PHQ-2 score collected",
				details: `${patientName} scored ${score} on the PHQ-2 questionnaire`
			};
		default:
			console.error(`Invalid psychological memory ${type}`);
			return;
	}
};

export type PsychologicalQuestion = {
	key: string;
	description: string;
}

export type Request_PatientData = {
	keys?: TableKeys[],
	unitId: string,
	startTime?: number,
	endTime?: number
}

export type Request_PatientDataForMany = {
	keys?: TableKeys[],
	unitIds: string[],
	startTime?: number,
	endTime?: number,
	memoryNames?: string[],
	orgsData: {[_id: string] : ObjectTS}
}

export type ReturnType_PatientData = {
	data: DB_PatientData[],
	share_info: { [subKey: string]: boolean }
};
export type PatientDataForManyCSV = {
	data: DB_PatientDataForCSV[],
	share_info: { [subKey: string]: boolean }
};

export type ReturnType_PatientDataForMany = {
	results: PatientDataForManyCSV[],
	eventsPerUnit: {[unitId: string]: CalendarEvent[]},
	eventsStatuses: {[eventId: string]: ReminderStatus[]}
}

export type DateRange = {
	startDate: number,
	endDate: number
}

export type FactAndValue = {
	fact: string
	value: string
}

export type Request_InsertData = {
	patient: { id: string, username: string },
	dateRange: DateRange,
	table: TableKeys,
	factAndValue: FactAndValue[]
}

export type Request_PatientLastUpdated = {
	id: string
}

export type Request_PatientsLastUpdated = {
	unitsId: string []
}
export type Response_PatientLastUpdate = {
    lastPositivePlanName?: string,
	timestamp?: number,
	lastStt?: string,
	lastPlan?: string,
	intent?: string
} | undefined

export type Response_PatientsLastUpdate = {
	id: number
	agentId: string,
	timestamp: number,
	planName: string,
    lastPositivePlanName?: string
}

